'use client';

import { createClient } from '@/lib/supabase/client';

export type AuditAction =
  | 'create' |'update' |'delete' |'login' |'logout' |'invite' |'role_change' |'stock_adjust' |'production_run';

export interface AuditEntry {
  companyId: string;
  userId: string;
  userEmail: string;
  userName: string;
  action: AuditAction;
  entityType: string;
  entityId?: string;
  entityName?: string;
  description: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
}

export async function logAudit(entry: AuditEntry): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('audit_log').insert({
      company_id: entry.companyId,
      user_id: entry.userId,
      user_email: entry.userEmail,
      user_name: entry.userName,
      action: entry.action,
      entity_type: entry.entityType,
      entity_id: entry.entityId ?? null,
      entity_name: entry.entityName ?? null,
      description: entry.description,
      old_values: entry.oldValues ?? null,
      new_values: entry.newValues ?? null,
    });
  } catch {
    // Audit logging should never break the main flow
  }
}

export interface AuditLogRow {
  id: string;
  companyId: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  action: AuditAction;
  entityType: string;
  entityId: string | null;
  entityName: string | null;
  description: string;
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
  createdAt: string;
}

export async function fetchAuditLog(
  companyId: string,
  options?: { limit?: number; entityType?: string; userId?: string }
): Promise<AuditLogRow[]> {
  const supabase = createClient();
  let query = supabase
    .from('audit_log')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    .limit(options?.limit ?? 200);

  if (options?.entityType) {
    query = query.eq('entity_type', options.entityType);
  }
  if (options?.userId) {
    query = query.eq('user_id', options.userId);
  }

  const { data, error } = await query;
  if (error) return [];

  return (data ?? []).map((row) => ({
    id: row.id,
    companyId: row.company_id,
    userId: row.user_id,
    userEmail: row.user_email,
    userName: row.user_name,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    entityName: row.entity_name,
    description: row.description,
    oldValues: row.old_values,
    newValues: row.new_values,
    createdAt: row.created_at,
  }));
}
